/**
 * Generic data list page for descriptor-driven domain routes.
 *
 * Renders a backend-driven table using either a typed resource hook or a
 * descriptor-bound TableDataSource. Raw backend errors remain in diagnostics
 * and are never rendered as user-facing reason codes.
 */
import { useCallback, useMemo } from 'react';
import { PageLoadingState, PageErrorState } from '../states';
import { Page, PageSection } from '../layout';
import { TableRenderer, type TableActionContext } from './TableRenderer';
import type { TableDataSource } from './table/table-data-source.js';
import { useAppI18n } from '../../i18n/use-app-i18n';
import type { TableDescriptor, ActionDescriptor, TranslationKey } from '@finance/contracts';
import type { ResolvedAction } from '../../runtime/actions/action-resolution';

interface ListHookResult {
  data?: unknown;
  isLoading: boolean;
  isError: boolean;
  error?: Error | null | unknown;
  refetch: () => void;
}

interface DataListPageProps<TRow extends Record<string, unknown> = Record<string, unknown>> {
  titleKey: TranslationKey;
  useList: () => ListHookResult;
  dataSource?: TableDataSource;
  tableInstanceKey?: string | number;
  rowIdExtractor: (row: TRow) => string | undefined;
  onRowActivate?: (row: TRow, resolvedAction?: ResolvedAction) => void;
  detailPath?: string;
  descriptor: TableDescriptor;
  onAction?: (
    action: ActionDescriptor,
    row: TRow,
    context: TableActionContext,
  ) => void | Promise<void>;
  onExport?: (options: {
    scope: 'page' | 'selection' | 'all';
    format: 'csv' | 'xlsx';
    fields?: string[];
  }) => void;
  rowActivationAction?: ActionDescriptor;
  createAction?: ActionDescriptor;
}

function isRecordArray(value: unknown): value is Record<string, unknown>[] {
  return Array.isArray(value) && value.every((item) => typeof item === 'object' && item !== null);
}

function isDataEnvelope(value: unknown): value is { data: unknown } {
  return typeof value === 'object' && value !== null && 'data' in value && (value as { data: unknown }).data !== undefined;
}

export function DataListPage<TRow extends Record<string, unknown> = Record<string, unknown>>({
  titleKey,
  useList,
  dataSource,
  tableInstanceKey,
  rowIdExtractor,
  onRowActivate,
  descriptor,
  onAction,
  onExport,
  rowActivationAction: rowActivationActionProp,
  createAction: createActionProp,
}: DataListPageProps<TRow>) {
  const { text } = useAppI18n();
  const { data: raw, isLoading, isError, refetch } = useList();
  const maybeRows = isDataEnvelope(raw) ? raw.data : raw;
  const dataMalformed = !dataSource && !isLoading && !isError && !isRecordArray(maybeRows);
  const rows = useMemo(
    () => (isRecordArray(maybeRows) ? maybeRows as TRow[] : []),
    [maybeRows],
  );
  const rowActivationAction = rowActivationActionProp ?? descriptor.rowActivationAction;
  const createAction = createActionProp ?? descriptor.createAction;
  const tableContract = useMemo<TableDescriptor>(() => ({
    ...descriptor,
    ...(rowActivationAction ? { rowActivationAction } : {}),
    ...(createAction ? { createAction } : {}),
    capabilities: {
      ...descriptor.capabilities,
      ...(createAction ? { create: true } : {}),
    },
  }), [descriptor, rowActivationAction, createAction]);

  const handleTableAction = useCallback(
    (resolved: ResolvedAction | undefined, context: TableActionContext) => {
      if (context.source === 'row-activation') {
        if (!resolved) onRowActivate?.((context.row ?? {}) as TRow);
        return;
      }
      if (resolved) {
        return onAction?.(resolved.action, (context.row ?? {}) as TRow, context);
      }
    },
    [onAction, onRowActivate],
  );

  if (!dataSource && isLoading) return <PageLoadingState />;
  if (!dataSource && (isError || dataMalformed)) return <PageErrorState onRetry={refetch} />;

  return (
    <Page
      title={text(titleKey)}
      subtitle={descriptor.descriptionKey ? text(descriptor.descriptionKey) : undefined}
    >
      <PageSection variant="plain" padding="none">
        <TableRenderer
          key={tableInstanceKey}
          contract={tableContract}
          dataSource={dataSource}
          data={dataSource ? undefined : rows}
          loading={dataSource ? undefined : isLoading}
          getRowId={rowIdExtractor as (row: Record<string, unknown>) => string | undefined}
          onAction={handleTableAction}
          onExport={onExport}
          onRetry={dataSource ? undefined : refetch}
        />
      </PageSection>
    </Page>
  );
}
